// Image export: a single still frame and a stroboscopic / time-lapse composite
// of the walk — the classic multi-exposure gait image. All on-device (canvas),
// nothing uploaded.

import { SKELETON_EDGES, BP, type PoseFrame } from "../pose/landmarks";
import { frameIndexAtMs } from "../viz/overlay";

const EDGE = "#7cc4ff";
const JOINT = "#f5f5f7";
const FOOT = "#5eead4";

export interface ExportMeta {
  label: string; // patient code
  sub: string; // e.g. "Lateral · 2026-06-12"
}

async function createVideo(url: string): Promise<HTMLVideoElement> {
  const v = document.createElement("video");
  v.src = url;
  v.muted = true;
  v.preload = "auto";
  (v as HTMLVideoElement & { playsInline: boolean }).playsInline = true;
  await new Promise<void>((res, rej) => {
    v.onloadedmetadata = () => res();
    v.onerror = () => rej(new Error("Could not read video for export"));
  });
  if (!isFinite(v.duration) || v.duration <= 0) {
    await new Promise<void>((res) => {
      const on = () => { v.removeEventListener("seeked", on); v.currentTime = 0; res(); };
      v.addEventListener("seeked", on);
      v.currentTime = 1e7;
    });
  }
  return v;
}

function seek(v: HTMLVideoElement, t: number): Promise<void> {
  return new Promise((res) => {
    const on = () => { v.removeEventListener("seeked", on); res(); };
    v.addEventListener("seeked", on);
    v.currentTime = Math.min(Math.max(t, 0), Math.max(0, v.duration - 1e-3));
  });
}

function drawSkeleton(
  ctx: CanvasRenderingContext2D,
  frame: PoseFrame,
  W: number,
  H: number,
  alpha = 1,
  minVis = 0.4,
) {
  const lm = frame.landmarks;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.lineWidth = Math.max(2, W * 0.004);
  ctx.strokeStyle = EDGE;
  for (const [a, b] of SKELETON_EDGES) {
    const pa = lm[a];
    const pb = lm[b];
    if (!pa || !pb || pa.visibility < minVis || pb.visibility < minVis) continue;
    ctx.beginPath();
    ctx.moveTo(pa.x * W, pa.y * H);
    ctx.lineTo(pb.x * W, pb.y * H);
    ctx.stroke();
  }
  const feet = new Set<number>([BP.LEFT_HEEL, BP.RIGHT_HEEL, BP.LEFT_FOOT_INDEX, BP.RIGHT_FOOT_INDEX, BP.LEFT_ANKLE, BP.RIGHT_ANKLE]);
  const r = Math.max(2.5, W * 0.005);
  for (let i = 0; i < lm.length; i++) {
    const p = lm[i];
    if (!p || p.visibility < minVis) continue;
    ctx.fillStyle = feet.has(i) ? FOOT : JOINT;
    ctx.beginPath();
    ctx.arc(p.x * W, p.y * H, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function watermark(ctx: CanvasRenderingContext2D, W: number, H: number, meta: ExportMeta) {
  const barH = Math.max(46, H * 0.09);
  const g = ctx.createLinearGradient(0, H - barH, 0, H);
  g.addColorStop(0, "rgba(0,0,0,0)");
  g.addColorStop(1, "rgba(0,0,0,0.72)");
  ctx.fillStyle = g;
  ctx.fillRect(0, H - barH, W, barH);

  const pad = W * 0.02;
  const fs = Math.max(16, W * 0.022);
  ctx.textBaseline = "alphabetic";
  ctx.font = `700 ${fs}px -apple-system, "SF Pro Display", system-ui, sans-serif`;
  ctx.fillStyle = "#7cc4ff";
  ctx.fillText("CADtomie", pad, H - pad);
  const wA = ctx.measureText("CADtomie ").width;
  ctx.fillStyle = "#f5f5f7";
  ctx.font = `300 ${fs}px -apple-system, "SF Pro Display", system-ui, sans-serif`;
  ctx.fillText("Gait", pad + wA, H - pad);

  ctx.font = `400 ${fs * 0.7}px -apple-system, system-ui, sans-serif`;
  ctx.fillStyle = "rgba(245,245,247,0.85)";
  ctx.textAlign = "right";
  ctx.fillText(`${meta.label}  ·  ${meta.sub}`, W - pad, H - pad);
  ctx.textAlign = "left";
}

function toBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error("export failed"))), "image/png"),
  );
}

export interface StillOptions {
  videoUrl: string;
  tMs: number;
  frames?: PoseFrame[];
  overlay?: boolean;
  meta: ExportMeta;
}

export async function buildStillPng(opts: StillOptions): Promise<Blob> {
  const v = await createVideo(opts.videoUrl);
  const W = v.videoWidth || 1280;
  const H = v.videoHeight || 720;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  await seek(v, opts.tMs / 1000);
  ctx.drawImage(v, 0, 0, W, H);
  if (opts.overlay && opts.frames?.length) {
    const f = opts.frames[frameIndexAtMs(opts.frames, opts.tMs)];
    if (f) drawSkeleton(ctx, f, W, H, 0.95);
  }
  watermark(ctx, W, H, opts.meta);
  return toBlob(canvas);
}

export interface MontageOptions {
  videoUrl: string;
  frames: PoseFrame[];
  startMs: number;
  endMs: number;
  samples?: number;
  overlay?: boolean;
  meta: ExportMeta;
}

/** Stroboscopic multi-exposure of the walk across one wide frame. */
export async function buildMontagePng(opts: MontageOptions): Promise<Blob> {
  const v = await createVideo(opts.videoUrl);
  const W = v.videoWidth || 1280;
  const H = v.videoHeight || 720;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  const n = Math.max(4, Math.min(opts.samples ?? 8, 14));
  const span = Math.max(0, opts.endMs - opts.startMs);

  // Base exposure (first sample) at full opacity.
  await seek(v, opts.startMs / 1000);
  ctx.drawImage(v, 0, 0, W, H);

  // Subsequent exposures, blended, with an optional brighter skeleton accent.
  for (let i = 1; i < n; i++) {
    const t = opts.startMs + (span * i) / (n - 1);
    await seek(v, t / 1000);
    ctx.globalAlpha = 0.5;
    ctx.drawImage(v, 0, 0, W, H);
    ctx.globalAlpha = 1;
    if (opts.overlay) {
      const f = opts.frames[frameIndexAtMs(opts.frames, t)];
      if (f) drawSkeleton(ctx, f, W, H, 0.85);
    }
  }
  watermark(ctx, W, H, opts.meta);
  return toBlob(canvas);
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
